// "Track this fare instead" helpers (Oct 2026).
//
// Every trip tracks exactly one fare: Best value or Direct only. Each check
// also stores the other one, so the alternative is already on the pin and
// costs nothing extra to show. These helpers only decide what to show; the
// backend (POST /trips/{id}/switch-fare) re-enforces every rule.

const SEARCH_BUDGET_PER_PIN = 25;

export function stopsOf(flight) {
  if (!flight) return null;
  const segs = flight.segments && flight.segments.length > 0 ? flight.segments : [flight];
  return Math.max(0, segs.length - 1);
}

// Returns null when there is nothing worth offering, otherwise
// { kind:'direct'|'best_value', price, rawPrice, flight, returnFlight, stops, trackedPrice, diff }
// fxRatio converts the raw Duffel price to the user's budget currency, the
// same way the Deal screen's own toComparable does.
export function getAltFare(pin, fxRatio = 1) {
  if (!pin) return null;
  const tracksDirect = pin.flight_preference === 'direct';
  const raw = tracksDirect ? pin.best_value_price : pin.direct_price;
  const trackedRaw = tracksDirect ? pin.direct_price : (pin.best_value_price || pin.last_price);
  // Oct 19: a Direct-only trip whose check found flights but no nonstop one has
  // no tracked price at all. That is exactly when the alternative matters most,
  // so it is still offered (noTrackedFare = true, diff = null), instead of null.
  if (!raw) return null;
  if (!trackedRaw && !tracksDirect) return null;
  const noTrackedFare = !trackedRaw;
  const flight = tracksDirect ? pin.best_value_flight : pin.direct_flight;
  const returnFlight = tracksDirect ? pin.best_value_flight_return : pin.direct_flight_return;
  if (!flight) return null;
  const altStops = stopsOf(flight);
  // Nothing to offer if it is the same ticket as the tracked one.
  const trackedFlight = tracksDirect ? pin.direct_flight : pin.best_value_flight;
  if (trackedFlight && flight.flight_number && trackedFlight.flight_number === flight.flight_number && raw === trackedRaw) return null;
  const price = raw * fxRatio;
  const trackedPrice = noTrackedFare ? null : trackedRaw * fxRatio;
  return {
    kind: tracksDirect ? 'best_value' : 'direct',
    price,
    rawPrice: raw,
    flight,
    returnFlight,
    stops: altStops,
    trackedPrice,
    diff: noTrackedFare ? null : price - trackedPrice,
    noTrackedFare,
  };
}

// Whether the trip itself may be switched (not whether an alternative exists).
// hasFreezeHistory must come from the caller (any Price Watch, active or past).
export function canSwitchFare(pin, hasFreezeHistory) {
  if (!pin) return false;
  if (pin.is_active === false) return false;
  if (pin.alt_switched_at) return false;
  if (pin.lifetime_cap_reached) return false;
  if ((pin.check_count || 0) >= SEARCH_BUDGET_PER_PIN) return false;
  if (hasFreezeHistory) return false;
  return true;
}

export function altTitle(alt) {
  if (!alt) return '';
  if (alt.noTrackedFare) {
    return alt.stops > 0 ? `No nonstop found. Available with ${alt.stops} stop${alt.stops === 1 ? '' : 's'}` : 'No nonstop found. Another option is available';
  }
  if (alt.kind === 'direct') return 'Nonstop is available';
  if (alt.stops === 0) return 'Another nonstop option';
  return `Cheaper with ${alt.stops} stop${alt.stops === 1 ? '' : 's'}`;
}